// src/components/tasks/TaskModal.js
// ✅ DROP-IN REPLACEMENT
//
// ✅ Keeps your existing fixes:
// - focus bug fix (no inner component declarations that cause remount loops)
// - footer submit button submits the form via form=...
// - no double submit
//
// ✅ Adds: Subtasks (checklist) WITHIN a task
// - Stored on task doc as: subtasks: [{ id, title, isDone, order, createdAt, completedAt, completedByUid }]
// - Add / toggle / edit / reorder / delete
// - Progress indicator + progress bar
//
// ✅ Preserves:
// - Centralized AttachmentsPanel (edit mode requires task.id)
// - TaskCommentsPanel (edit mode only)
// - Follow-up indicator for sample orders

import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Plus,
  Users as UsersIcon,
  Paperclip,
  Repeat,
  Info,
  CheckSquare,
  Square,
  Trash2,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

import { TaskService } from "./TaskService";
import TaskCommentsPanel from "./TaskCommentsPanel";
import AttachmentsPanel from "../attachments/AttachmentsPanel";

const TASK_TYPES = ["General", "Call", "Meeting", "Email", "Follow-up", "Delivery", "Installation"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUS_OPTIONS = ["To Do", "In Progress", "Completed", "On Hold", "Cancelled"];

// ✅ IMPORTANT: normalize to lowercase
const STAFF_ROLES = new Set(["owner", "admin", "enduser"]);

const RECURRENCE_PRESETS = [
  { value: "None", label: "None" },
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "Bi-Weekly", label: "Bi-Weekly" },
  { value: "Monthly", label: "Monthly" },
  { value: "Custom", label: "Custom..." },
];

function displayUser(u) {
  return (u?.fullName || u?.name || u?.displayName || u?.email || u?.id || "User").toString().trim();
}

function normalizeRole(role) {
  return (role || "").toString().trim().toLowerCase().replace(/[_\s-]+/g, "");
}

function normalizeUid(value) {
  return (value || "").toString().trim();
}

function roleLabel(role) {
  const r = normalizeRole(role);
  if (r === "owner") return "Owner";
  if (r === "admin") return "Admin";
  if (r === "enduser") return "User";
  return role || "User";
}

function needsStrictAssignment(type) {
  const normalized = (type || "").toString().trim().toLowerCase();
  return normalized && normalized !== "general";
}

// -------------------- Subtasks helpers --------------------
function makeSubtaskId() {
  try {
    // modern browsers
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  // fallback
  return `st_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

function normalizeSubtasksFromTask(task) {
  if (!task) return [];

  // Primary: task.subtasks (array)
  const raw =
    Array.isArray(task.subtasks)
      ? task.subtasks
      : Array.isArray(task.checklist)
      ? task.checklist
      : Array.isArray(task.checklistItems)
      ? task.checklistItems
      : null;

  if (Array.isArray(raw)) {
    return raw
      .map((s, idx) => {
        const id = (s?.id || s?.subtaskId || "").toString().trim() || makeSubtaskId();
        const title = (s?.title || s?.text || s?.name || "").toString();
        const isDone = !!(s?.isDone ?? s?.done ?? s?.completed);
        const order = Number.isFinite(Number(s?.order)) ? Number(s.order) : idx;

        return {
          id,
          title,
          isDone,
          order,
          createdAt: s?.createdAt || null,
          completedAt: s?.completedAt || null,
          completedByUid: s?.completedByUid || null,
        };
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  // Secondary: object-map style (rare)
  if (task.subtasks && typeof task.subtasks === "object" && !Array.isArray(task.subtasks)) {
    return Object.entries(task.subtasks)
      .map(([k, v], idx) => {
        const id = (v?.id || k || "").toString().trim() || makeSubtaskId();
        const title = (v?.title || v?.text || v?.name || "").toString();
        const isDone = !!(v?.isDone ?? v?.done ?? v?.completed);
        const order = Number.isFinite(Number(v?.order)) ? Number(v.order) : idx;

        return {
          id,
          title,
          isDone,
          order,
          createdAt: v?.createdAt || null,
          completedAt: v?.completedAt || null,
          completedByUid: v?.completedByUid || null,
        };
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  return [];
}

function sanitizeSubtasksForSave(subtasks, meUid) {
  const safe = Array.isArray(subtasks) ? subtasks : [];
  const cleaned = safe
    .map((s, idx) => {
      const id = (s?.id || "").toString().trim() || makeSubtaskId();
      const title = (s?.title || "").toString().trim();
      if (!title) return null;

      const isDone = !!s?.isDone;
      const createdAt = s?.createdAt || new Date().toISOString();

      // Keep completion metadata if already present; set if newly completed.
      let completedAt = s?.completedAt || null;
      let completedByUid = s?.completedByUid || null;

      if (isDone) {
        if (!completedAt) completedAt = new Date().toISOString();
        if (!completedByUid) completedByUid = meUid || null;
      } else {
        // if reopened, clear completion metadata (common UX)
        completedAt = null;
        completedByUid = null;
      }

      return {
        id,
        title,
        isDone,
        order: idx,
        createdAt,
        completedAt,
        completedByUid,
      };
    })
    .filter(Boolean);

  return cleaned;
}

// ✅ CRITICAL FIX: component must NOT be declared inside TaskModal (focus bug)
const FieldRow = React.memo(function FieldRow({ label, required, children }) {
  return (
    <div className="min-w-0">
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[6.5rem_minmax(0,1fr)] sm:items-center sm:gap-2">
        <label className="text-[11px] font-medium text-slate-700 sm:pt-0.5">
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>
        <div className="min-w-0">{children}</div>
      </div>
    </div>
  );
});

// ✅ Also keep subtask row component OUTSIDE TaskModal (prevents remount/focus issues)
const SubtaskRow = React.memo(function SubtaskRow({
  subtask,
  index,
  total,
  onToggleDone,
  onChangeTitle,
  onRemove,
  onMoveUp,
  onMoveDown,
}) {
  const isFirst = index === 0;
  const isLast = index === total - 1;

  return (
    <li className="flex items-center gap-2 px-2 py-2 hover:bg-slate-50">
      <button
        type="button"
        onClick={() => onToggleDone(subtask.id)}
        className="shrink-0 h-8 w-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center"
        title={subtask.isDone ? "Mark not done" : "Mark done"}
      >
        {subtask.isDone ? (
          <CheckSquare className="w-4 h-4 text-emerald-700" />
        ) : (
          <Square className="w-4 h-4 text-slate-500" />
        )}
      </button>

      <input
        type="text"
        value={subtask.title}
        onChange={(e) => onChangeTitle(subtask.id, e.target.value)}
        className={`flex-1 min-w-0 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          subtask.isDone ? "bg-emerald-50/40 text-slate-600 line-through" : "bg-white"
        }`}
        placeholder="Subtask…"
      />

      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => onMoveUp(subtask.id)}
          disabled={isFirst}
          className="h-8 w-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          title="Move up"
        >
          <ChevronUp className="w-4 h-4 text-slate-600" />
        </button>
        <button
          type="button"
          onClick={() => onMoveDown(subtask.id)}
          disabled={isLast}
          className="h-8 w-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          title="Move down"
        >
          <ChevronDown className="w-4 h-4 text-slate-600" />
        </button>
        <button
          type="button"
          onClick={() => onRemove(subtask.id)}
          className="h-8 w-8 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 flex items-center justify-center"
          title="Delete subtask"
        >
          <Trash2 className="w-4 h-4 text-red-700" />
        </button>
      </div>
    </li>
  );
});

const TaskModal = ({
  mode, // "create" | "edit"
  companyId,
  me,
  task,
  onClose,
  onSaved,
  usersMap = {},
  accountsMap = {},
  groupsMap = {},
}) => {
  const isEdit = mode === "edit";

  // ✅ Needed because footer button is outside form
  const FORM_ID = "task-modal-form";

  const staffUsers = useMemo(() => {
    return Object.values(usersMap)
      .map((u) => ({
        ...u,
        // ✅ enforce uid presence
        uid: u.uid || u.id,
        id: u.id || u.uid,
      }))
      .filter((u) => STAFF_ROLES.has((u.role || "").toString().trim().toLowerCase()))
      .sort((a, b) => displayUser(a).localeCompare(displayUser(b)));
  }, [usersMap]);

  const groups = useMemo(() => {
    return Object.entries(groupsMap)
      .map(([id, g]) => ({ id, ...(g || {}) }))
      .sort((a, b) => (a.name || a.title || "").localeCompare(b.name || b.title || ""));
  }, [groupsMap]);

  const meUid = me?.uid || me?.id || null;
  const canManageAssignments = ["owner", "admin"].includes(normalizeRole(me?.role));
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "To Do",
    priority: "Medium",
    type: "General",
    dueDate: "",
    dueTime: "",
    recurrence: "None",
    customRecurrence: "",
    convertedToOrderOnComplete: false,
    accountId: "",
    estimatedHours: 1,

    assignMode: "users",
    assignedToUserIds: [],
    groupId: "",
    soloCopies: false,

    // ✅ NEW: Subtasks
    subtasks: [],
  });

  const visibleAccounts = useMemo(() => {
    const selectedAccountId = formData.accountId || task?.accountId || "";
    const entries = Object.entries(accountsMap || {});
    const filtered = entries.filter(([id, account]) => {
      if (canManageAssignments) return true;
      if (!account) return false;
      if (id === selectedAccountId) return true;
      const assignedToId = normalizeUid(account.assignedToId || account.assignedRepId || account.ownerUid);
      const createdByUid = normalizeUid(account.createdByUid || account.createdBy || account.ownerCreatedByUid);
      return assignedToId === normalizeUid(meUid) || createdByUid === normalizeUid(meUid);
    });

    return filtered.sort(([, a], [, b]) =>
      String(a?.name || a?.title || "")
        .toLowerCase()
        .localeCompare(String(b?.name || b?.title || "").toLowerCase())
    );
  }, [accountsMap, canManageAssignments, formData.accountId, meUid, task?.accountId]);

  const [userSearch, setUserSearch] = useState("");
  const [newSubtaskText, setNewSubtaskText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toValidDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "string") {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof value?.toDate === "function") {
      const parsed = value.toDate();
      return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
    }
    if (typeof value?.seconds === "number") {
      const parsed = new Date(value.seconds * 1000);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  useEffect(() => {
    if (isEdit && task) {
      const dueAtDate = toValidDate(task.dueAt);
      const dueDate = dueAtDate ? dueAtDate.toISOString().split("T")[0] : "";

      let dueTime = "";
      if (dueAtDate) {
        const hh = String(dueAtDate.getHours()).padStart(2, "0");
        const mm = String(dueAtDate.getMinutes()).padStart(2, "0");
        dueTime = `${hh}:${mm}`;
      }

      const subtasks = normalizeSubtasksFromTask(task);

      setFormData({
        title: task.title || "",
        description: task.description || "",
        status: task.status || "To Do",
        priority: task.priority || "Medium",
        type: task.type || "General",
        dueDate,
        dueTime,
        recurrence: task.recurrence || "None",
        customRecurrence: task.customRecurrence || "",
        convertedToOrderOnComplete: !!task.convertedToOrderOnComplete,
        accountId: task.accountId || "",
        estimatedHours: task.estimatedHours || 1,

        assignMode: task.assignMode || "users",
        assignedToUserIds: Array.isArray(task.assignedToUserIds) ? task.assignedToUserIds : [],
        groupId: task.groupId || "",
        soloCopies: task.distribution === "soloCopies",

        subtasks,
      });

      setNewSubtaskText("");
    }

    // If creating, keep defaults but ensure subtasks empty
    if (!isEdit && mode === "create") {
      setNewSubtaskText("");
    }
  }, [isEdit, task, mode]);

  useEffect(() => {
    if (!canManageAssignments && meUid) {
      setFormData((prev) => ({
        ...prev,
        assignMode: "users",
        assignedToUserIds: [meUid],
        groupId: "",
        soloCopies: false,
      }));
    }
  }, [canManageAssignments, meUid]);

  const selectedGroupName = formData.groupId
    ? groupsMap?.[formData.groupId]?.name || groupsMap?.[formData.groupId]?.title || ""
    : "";

  const selectedAccount = useMemo(() => {
    const id = formData.accountId || task?.accountId || "";
    return id ? accountsMap?.[id] || null : null;
  }, [formData.accountId, task?.accountId, accountsMap]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "type") {
      setFormData((prev) => ({
        ...prev,
        type: value,
        recurrence: value === "Follow-up" ? "None" : prev.recurrence,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleUser = (userId) => {
    setFormData((prev) => {
      const current = new Set(prev.assignedToUserIds);
      if (current.has(userId)) current.delete(userId);
      else current.add(userId);
      return { ...prev, assignedToUserIds: Array.from(current) };
    });
  };

  const clearUsers = () => {
    setFormData((prev) => ({ ...prev, assignedToUserIds: [] }));
    setUserSearch("");
  };

  const filteredUsers = useMemo(() => {
    const q = (userSearch || "").toLowerCase().trim();
    if (!q) return staffUsers;
    return staffUsers.filter((u) => displayUser(u).toLowerCase().includes(q));
  }, [staffUsers, userSearch]);

  // -------------------- Subtasks handlers --------------------
  const addSubtask = () => {
    const title = (newSubtaskText || "").trim();
    if (!title) return;

    setFormData((prev) => {
      const next = Array.isArray(prev.subtasks) ? [...prev.subtasks] : [];
      next.push({
        id: makeSubtaskId(),
        title,
        isDone: false,
        order: next.length,
        createdAt: new Date().toISOString(),
        completedAt: null,
        completedByUid: null,
      });
      return { ...prev, subtasks: next };
    });

    setNewSubtaskText("");
  };

  const toggleSubtaskDone = (subtaskId) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.subtasks) ? prev.subtasks : [];
      const next = list.map((s) => {
        if (s.id !== subtaskId) return s;
        const nextDone = !s.isDone;

        return {
          ...s,
          isDone: nextDone,
          completedAt: nextDone ? s.completedAt || new Date().toISOString() : null,
          completedByUid: nextDone ? s.completedByUid || me?.uid || null : null,
        };
      });
      return { ...prev, subtasks: next };
    });
  };

  const changeSubtaskTitle = (subtaskId, title) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.subtasks) ? prev.subtasks : [];
      const next = list.map((s) => (s.id === subtaskId ? { ...s, title } : s));
      return { ...prev, subtasks: next };
    });
  };

  const removeSubtask = (subtaskId) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.subtasks) ? prev.subtasks : [];
      const next = list.filter((s) => s.id !== subtaskId).map((s, idx) => ({ ...s, order: idx }));
      return { ...prev, subtasks: next };
    });
  };

  const moveSubtaskUp = (subtaskId) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.subtasks) ? [...prev.subtasks] : [];
      const idx = list.findIndex((s) => s.id === subtaskId);
      if (idx <= 0) return prev;

      const tmp = list[idx - 1];
      list[idx - 1] = list[idx];
      list[idx] = tmp;

      const next = list.map((s, i) => ({ ...s, order: i }));
      return { ...prev, subtasks: next };
    });
  };

  const moveSubtaskDown = (subtaskId) => {
    setFormData((prev) => {
      const list = Array.isArray(prev.subtasks) ? [...prev.subtasks] : [];
      const idx = list.findIndex((s) => s.id === subtaskId);
      if (idx === -1 || idx >= list.length - 1) return prev;

      const tmp = list[idx + 1];
      list[idx + 1] = list[idx];
      list[idx] = tmp;

      const next = list.map((s, i) => ({ ...s, order: i }));
      return { ...prev, subtasks: next };
    });
  };

  const subtaskStats = useMemo(() => {
    const list = Array.isArray(formData.subtasks) ? formData.subtasks : [];
    const total = list.length;
    const done = list.filter((s) => !!s.isDone).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return { total, done, pct };
  }, [formData.subtasks]);

  const validate = () => {
    if (!formData.title.trim()) return "Title is required.";
    if (!formData.dueDate) return "Due date is required.";

    if (formData.type !== "Follow-up" && formData.recurrence === "Custom" && !formData.customRecurrence.trim()) {
      return "Please specify custom recurrence pattern.";
    }

    if (canManageAssignments && formData.assignMode === "group" && !formData.groupId) {
      return "Please select a group.";
    }

    if (
      canManageAssignments &&
      needsStrictAssignment(formData.type) &&
      formData.assignMode === "users" &&
      (!Array.isArray(formData.assignedToUserIds) || formData.assignedToUserIds.length === 0)
    ) {
      return "Please assign this task to a user or group.";
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const cleanedSubtasks = sanitizeSubtasksForSave(formData.subtasks, me?.uid || null);

      const normalizedAssignment = !canManageAssignments
        ? {
            assignMode: "users",
            assignedToUserIds: meUid ? [meUid] : [],
            groupId: "",
            groupName: "",
            soloCopies: false,
          }
        : formData.assignMode === "group"
        ? {
            assignMode: "group",
            assignedToUserIds: [],
            groupId: formData.groupId,
            groupName: selectedGroupName,
            soloCopies: true,
          }
        : {
            assignMode: "users",
            assignedToUserIds: formData.assignedToUserIds,
            groupId: "",
            groupName: "",
            soloCopies: false,
          };

      const basePayload = {
        ...formData,
        recurrence: formData.type === "Follow-up" ? "None" : formData.recurrence,
        ...normalizedAssignment,

        // ✅ ensure clean, ordered subtasks
        subtasks: cleanedSubtasks,
      };

      const audit = {
        updatedByUid: me?.uid || null,
        updatedByRole: (me?.role || "endUser").toString().trim(),
      };

      const createAudit = {
        createdByUid: me?.uid || null,
        createdByRole: (me?.role || "endUser").toString().trim(),
      };

      const payload = isEdit ? { ...basePayload, ...audit } : { ...basePayload, ...createAudit, ...audit };

      if (isEdit && task?.id) {
        await TaskService.updateTask(companyId, task.id, payload);
      } else {
        await TaskService.createTask(companyId, payload);
      }

      onSaved?.();
    } catch (err) {
      console.error("Task save error:", err);
      setError(err?.message || "Failed to save task. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !task?.id) return;

    const confirmed = window.confirm("Are you sure you want to delete this task?");
    if (!confirmed) return;

    setSubmitting(true);
    setError("");

    try {
      await TaskService.deleteTask(companyId, task.id);
      onSaved?.();
    } catch (err) {
      console.error("Task delete error:", err);
      setError(err?.message || "Failed to delete task.");
      setSubmitting(false);
    }
  };

  const assignmentHint = !canManageAssignments
    ? "Tasks you create are automatically assigned to you."
    : formData.assignMode === "users"
    ? formData.assignedToUserIds.length > 1
      ? "Multi-user Joint: each assignee tracks their own status on one shared task."
      : "Single user task (or leave unassigned)."
    : "Group assignment creates an individual task for each member.";

  const filteredSubtasks = useMemo(() => {
    const list = Array.isArray(formData.subtasks) ? formData.subtasks : [];
    return [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [formData.subtasks]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-hidden p-0 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div
        className="relative flex max-h-[94vh] w-full max-w-[72rem] flex-col overflow-hidden overflow-x-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
        style={{ touchAction: "pan-y pinch-zoom" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:py-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-600/10 flex items-center justify-center">
              <Plus className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <div className="text-base font-semibold text-slate-900">{isEdit ? "Edit Task" : "Create Task"}</div>
              <div className="text-xs text-slate-500">{assignmentHint}</div>
            </div>
          </div>

          <button type="button" onClick={onClose} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Body */}
        <form id={FORM_ID} onSubmit={handleSubmit} className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2.5 sm:px-4 sm:py-4">
          {error && (
            <div className="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(20rem,0.9fr)]">
            {/* LEFT/MIDDLE */}
            <div className="space-y-2.5 sm:space-y-3">
              {/* Task Info */}
              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <div className="mb-2 text-sm font-semibold text-slate-900">Task Details</div>

                <div className="space-y-1.5 sm:space-y-2">
                  <FieldRow label="Title" required>
                    <input
                      type="text"
                      name="title"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Task title..."
                      required
                    />
                  </FieldRow>

                  {/* 🔥 Follow-up task indicator for sample orders */}
                  {isEdit && task?.sampleOrderId && (
                    <div className="mb-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Info className="w-4 h-4 text-blue-700" />
                        <div className="text-xs font-semibold text-blue-900">Follow-up Task</div>
                      </div>
                      <div className="text-xs text-blue-800">
                        This task is linked to sample order #{task.sampleOrderId}.
                        {task.status === "Completed" ? (
                          <span className="font-medium"> ✓ This task has been completed and the sample order has been updated.</span>
                        ) : (
                          <span> When marked as completed, these notes will be saved to the sample order.</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[6.5rem_minmax(0,1fr)] sm:items-start sm:gap-2">
                    <label className="text-[11px] font-medium text-slate-700 sm:pt-2">Description</label>
                    <textarea
                      name="description"
                      className="w-full min-w-0 resize-y rounded-xl border border-slate-300 px-3 py-3 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={7}
                      value={formData.description}
                      onChange={handleChange}
                      placeholder={
                        task?.sampleOrderId
                          ? "Add follow-up notes that will be saved to the sample order..."
                          : "Add details, steps, expectations..."
                      }
                    />
                  </div>

                  <FieldRow label="Type">
                    <select
                      name="type"
                      className="w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.type}
                      onChange={handleChange}
                    >
                      {TASK_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </FieldRow>

                  <div className="grid grid-cols-2 gap-2">
                    <FieldRow label="Priority">
                      <select
                        name="priority"
                        className="w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.priority}
                        onChange={handleChange}
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </FieldRow>

                    <FieldRow label="Est. Hours">
                      <input
                        type="number"
                        name="estimatedHours"
                        min="0.5"
                        step="0.5"
                        className="w-full min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.estimatedHours}
                        onChange={handleChange}
                      />
                    </FieldRow>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <FieldRow label="Due Date" required>
                      <input
                        type="date"
                        name="dueDate"
                        className="w-full min-w-0 max-w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.dueDate}
                        onChange={handleChange}
                        required
                      />
                    </FieldRow>

                    <FieldRow label="Due Time">
                      <input
                        type="time"
                        name="dueTime"
                        className="w-full min-w-0 max-w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.dueTime}
                        onChange={handleChange}
                      />
                    </FieldRow>
                  </div>

                  {formData.type !== "Follow-up" ? (
                    <FieldRow label="Recurrence">
                      <div className="flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <select
                          name="recurrence"
                          className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          value={formData.recurrence}
                          onChange={handleChange}
                        >
                          {RECURRENCE_PRESETS.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </FieldRow>
                  ) : null}

                  {formData.type !== "Follow-up" && formData.recurrence === "Custom" && (
                    <FieldRow label="Pattern">
                      <input
                        type="text"
                        name="customRecurrence"
                        className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={formData.customRecurrence}
                        onChange={handleChange}
                        placeholder="e.g. 3rd Friday of every month, 2nd Tuesday weekly"
                      />
                    </FieldRow>
                  )}

                  {formData.type === "Follow-up" && task?.sampleOrderId ? (
                    <FieldRow label="Follow-up outcome">
                      <label className="flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-3 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          name="convertedToOrderOnComplete"
                          checked={!!formData.convertedToOrderOnComplete}
                          onChange={handleChange}
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600"
                        />
                        <span>
                          <span className="font-medium text-slate-900">Converted to order?</span>
                          <span className="block text-xs text-slate-500">
                            Completing this task will mark the linked sample request as converted to order when checked,
                            or just close it out when left unchecked.
                          </span>
                        </span>
                      </label>
                    </FieldRow>
                  ) : null}

                  <FieldRow label="Account">
                    <select
                      name="accountId"
                      className="w-full min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      value={formData.accountId}
                      onChange={handleChange}
                    >
                      <option value="">None</option>
                      {visibleAccounts.map(([id, a]) => (
                        <option key={id} value={id}>
                          {a.name || a.title || id}
                        </option>
                      ))}
                    </select>
                  </FieldRow>
                </div>
              </div>

              {/* ✅ NEW: Subtasks */}
              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Subtasks</div>
                    <div className="text-xs text-slate-500">
                      {subtaskStats.total === 0
                        ? "Add a checklist to break this task down."
                        : `${subtaskStats.done}/${subtaskStats.total} completed (${subtaskStats.pct}%)`}
                    </div>
                  </div>

                  {subtaskStats.total > 0 && (
                    <div className="shrink-0 w-32">
                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                        <div
                          className="h-full bg-emerald-500"
                          style={{ width: `${subtaskStats.pct}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="mb-3 flex items-center gap-2">
                  <input
                    type="text"
                    value={newSubtaskText}
                    onChange={(e) => setNewSubtaskText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addSubtask();
                      }
                    }}
                    className="flex-1 min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add a subtask…"
                  />
                  <button
                    type="button"
                    onClick={addSubtask}
                    className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
                    disabled={!newSubtaskText.trim()}
                    title="Add subtask"
                  >
                    Add
                  </button>
                </div>

                {filteredSubtasks.length === 0 ? (
                  <div className="text-xs text-slate-500 py-3 text-center border border-dashed border-slate-200 rounded-lg">
                    No subtasks yet.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <ul className="divide-y divide-slate-100">
                      {filteredSubtasks.map((s, idx) => (
                        <SubtaskRow
                          key={s.id}
                          subtask={s}
                          index={idx}
                          total={filteredSubtasks.length}
                          onToggleDone={toggleSubtaskDone}
                          onChangeTitle={changeSubtaskTitle}
                          onRemove={removeSubtask}
                          onMoveUp={moveSubtaskUp}
                          onMoveDown={moveSubtaskDown}
                        />
                      ))}
                    </ul>
                  </div>
                )}

                {filteredSubtasks.length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-500">
                    Tip: Press <span className="font-mono">Enter</span> to add quickly.
                  </div>
                )}
              </div>

              {/* ✅ CENTRALIZED Attachments (separate /attachments collection) */}
              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-slate-700" />
                  <div className="text-sm font-semibold text-slate-900">Attachments</div>
                </div>

                {!isEdit || !task?.id ? (
                  <div className="text-xs text-slate-500">Save the task first to upload attachments.</div>
                ) : (
                  <AttachmentsPanel
                    companyId={companyId}
                    parentType="task"
                    parentId={task.id}
                    currentUserId={me?.uid || null}
                    readOnly={false}
                    title="Attachments"
                    accountId={formData.accountId || task.accountId || null}
                    accountName={(selectedAccount?.name || selectedAccount?.title || "").trim() || null}
                    pageSize={50}
                  />
                )}
              </div>

            </div>

            {/* RIGHT */}
            <div className="space-y-3">
              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <div className="mb-2 text-sm font-semibold text-slate-900">Status</div>
                <FieldRow label="Task status">
                  <select
                    name="status"
                    className="w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </FieldRow>
              </div>

              {/* Assignment */}
              <div className={canManageAssignments ? "rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4" : ""}>
                {canManageAssignments ? (
                  <div className="mb-2 text-sm font-semibold text-slate-900">Assignment</div>
                ) : null}

                {!canManageAssignments ? (
                  <FieldRow label="Assigned To">
                    <div className="px-0 py-1 text-sm font-semibold text-slate-900">
                      {displayUser(me || {})}
                    </div>
                  </FieldRow>
                ) : (<>
                <div className="mb-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, assignMode: "users", groupId: "" }))}
                    className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium ${
                      formData.assignMode === "users"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white border-slate-300"
                    }`}
                  >
                    Users
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({
                        ...p,
                        assignMode: "group",
                        assignedToUserIds: [],
                      }))
                    }
                    className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium ${
                      formData.assignMode === "group"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white border-slate-300"
                    }`}
                  >
                    Group
                  </button>
                </div>

                {formData.assignMode === "users" ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <UsersIcon className="w-4 h-4 text-blue-700" />
                        <div className="text-xs font-medium text-slate-900">Assign Users</div>
                      </div>
                      {formData.assignedToUserIds.length > 0 && (
                        <button
                          type="button"
                          onClick={clearUsers}
                          className="text-[11px] px-2 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <div className="text-xs text-slate-600 mb-2">
                      Selected:{" "}
                      <span className="font-semibold text-blue-600">{formData.assignedToUserIds.length}</span>
                    </div>

                    {staffUsers.length > 0 && (
                      <input
                        type="text"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        placeholder="Search users..."
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}

                    {staffUsers.length === 0 ? (
                      <div className="text-xs text-slate-500 text-center py-4">No users found.</div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="text-xs text-slate-500 text-center py-4">No users match your search.</div>
                    ) : (
                      <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white">
                        <ul className="divide-y divide-slate-100">
                          {filteredUsers.map((u) => {
                            const userId = u.uid || u.id;
                            const checked = formData.assignedToUserIds.includes(userId);

                            return (
                              <li key={userId} className="px-3 py-2 hover:bg-slate-50">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4"
                                    checked={checked}
                                    onChange={() => toggleUser(userId)}
                                  />
                                  <span className="flex-1">
                                    {displayUser(u)}
                                    <span className="ml-2 text-xs text-slate-500">({roleLabel(u.role)})</span>
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-2">Select Group</label>
                      <select
                        name="groupId"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        value={formData.groupId}
                        onChange={handleChange}
                      >
                        <option value="">Select...</option>
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name || g.title || g.id}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                      Every member in this group will get their own copy of the task.
                    </div>
                  </div>
                )}
                </>)}
              </div>

              {/* ✅ Comments (edit mode only) */}
              {isEdit && task?.id && (
                <TaskCommentsPanel
                  companyId={companyId}
                  taskId={task.id}
                  taskTitle={formData.title || task.title || "Task"}
                  me={me}
                  usersMap={usersMap}
                />
              )}

              {isEdit ? (
                <div className="border-t border-slate-200 pt-4 sm:pt-5">
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
                    disabled={submitting}
                  >
                    Delete Task
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 flex flex-row items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50"
            disabled={submitting}
          >
            Cancel
          </button>

          {/* ✅ IMPORTANT: footer button is OUTSIDE form so we must link it */}
          <button
            type="submit"
            form={FORM_ID}
            className="rounded-xl bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            disabled={submitting}
          >
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create Task"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
